import { coerceDeckTemplateSettings, type DeckTemplateSettings } from '../../deck/templateSettings'

const TEMPLATE_PREFIX = 'perlingo.deckTemplateSettings.'
const RETRY_PREFIX = 'perlingo.deckplayer.retryRequested.'

/**
 * localStorage keys that may hold unsaved deck template settings from the deck
 * editor: the bootstrap id, the prop id and their numeric forms.
 */
export function templateStorageKeys(deckId: string, fallbackDeckId: string): string[] {
  const ids = [String(deckId || '').trim(), String(fallbackDeckId || '').trim()].filter(Boolean)
  const numeric = ids.join(' ').match(/\d+/g)?.pop() ?? null
  const keys = new Set<string>()
  for (const id of ids) keys.add(`${TEMPLATE_PREFIX}${id}`)
  if (numeric) {
    keys.add(`${TEMPLATE_PREFIX}${numeric}`)
    keys.add(`${TEMPLATE_PREFIX}deck-${numeric}`)
  }
  return Array.from(keys)
}

export function readLocalDeckTemplate(
  deckId: string,
  fallbackDeckId: string,
): DeckTemplateSettings {
  try {
    for (const key of templateStorageKeys(deckId, fallbackDeckId)) {
      const raw = window.localStorage.getItem(key)
      if (!raw) continue
      const parsed = coerceDeckTemplateSettings(JSON.parse(raw) as unknown)
      if (Object.keys(parsed).length) return parsed
    }
    return {}
  } catch {
    return {}
  }
}

/**
 * Backend settings are authoritative; local ones only fill keys the backend leaves
 * unset, so stale entries (e.g. from a PDF import) never override saved settings.
 */
export function mergeDeckTemplate(
  backend: unknown,
  local: DeckTemplateSettings,
): DeckTemplateSettings {
  const result: Record<string, unknown> = {
    ...(coerceDeckTemplateSettings(backend) as Record<string, unknown>),
  }
  for (const [key, value] of Object.entries(local)) {
    if (value !== undefined && result[key] === undefined) result[key] = value
  }
  return result as DeckTemplateSettings
}

const retryKey = (deckId: string) => `${RETRY_PREFIX}${String(deckId || '').trim()}`

/** Retry flag fallback for visitors without stored progress. */
export function readLocalRetryRequested(deckId: string): boolean {
  try {
    const raw = window.localStorage.getItem(retryKey(deckId))
    if (!raw) return false
    const v = raw.trim().toLowerCase()
    return v === '1' || v === 'true' || v === 'yes' || v === 'on'
  } catch {
    return false
  }
}

export function writeLocalRetryRequested(deckId: string, value: boolean) {
  try {
    if (value) window.localStorage.setItem(retryKey(deckId), '1')
    else window.localStorage.removeItem(retryKey(deckId))
  } catch {
    // ignore
  }
}

const flowEndKey = (deckId: string, model: string) =>
  `perlingo.deckplayer.flowEndAt.${deckId}_${model}`

/** When this deck's flow last ended (or reached a phase checkpoint) in this browser. */
export function readFlowEndAt(deckId: string, model: string): number | null {
  try {
    const raw = window.localStorage.getItem(flowEndKey(deckId, model))
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

export function writeFlowEndAt(deckId: string, model: string, timestamp: number) {
  try {
    window.localStorage.setItem(flowEndKey(deckId, model), String(timestamp))
  } catch {
    // ignore
  }
}

export function clearFlowEndAt(deckId: string, model: string) {
  try {
    window.localStorage.removeItem(flowEndKey(deckId, model))
  } catch {
    // ignore
  }
}

export const isToday = (timestamp: number): boolean => {
  const d = new Date(timestamp)
  const n = new Date()
  return (
    d.getFullYear() === n.getFullYear() &&
    d.getMonth() === n.getMonth() &&
    d.getDate() === n.getDate()
  )
}

/** 23:59 local time today, as ISO string. */
export const midnightTonight = (): string => {
  const d = new Date()
  d.setHours(23, 59, 0, 0)
  return d.toISOString()
}
